// Shared, framework-agnostic helpers used by both the API (apps/api, plain Node
// ESM) and the web client (apps/web, Vite/TS). Keep this plain JavaScript so the
// API can import it at runtime with no build/transpile step.

// Names we never let a user register: brand/role impersonation risks, words that
// collide with app routes, and a few "system" handles. Compared case-insensitively
// against the lowercased handle.
export const RESERVED_USERNAMES = new Set([
  'admin', 'administrator', 'root', 'superuser', 'sysadmin', 'support', 'help', 'helpdesk',
  'mod', 'moderator', 'staff', 'official', 'team', 'echo', 'echologin', 'system', 'bot',
  'api', 'app', 'www', 'mail', 'email', 'noreply', 'no-reply', 'postmaster', 'webmaster',
  'owner', 'everyone', 'here', 'channel', 'dm', 'dms', 'null', 'undefined', 'none', 'anonymous',
  'login', 'logout', 'signin', 'signout', 'signup', 'register', 'auth', 'oauth', 'settings',
  'profile', 'profiles', 'user', 'users', 'me', 'account', 'accounts', 'terms', 'privacy',
  'about', 'contact', 'home', 'dashboard', 'search', 'explore', 'notifications',
]);

/**
 * Validate a username/handle. Returns an error string, or null when valid.
 *
 * Rules (industry-standard handle conventions): 3–30 chars; lowercase letters,
 * digits, and the separators `. _ -`; must start and end with a letter or digit;
 * no consecutive separators; not a reserved name. The handle is matched
 * case-insensitively (stored lowercase), while the display name keeps its casing.
 *
 * @param {string} raw
 * @returns {string | null}
 */
export function validateUsername(raw) {
  const uname = String(raw || '').trim().toLowerCase();
  if (uname.length < 3 || uname.length > 30) return 'Username must be 3–30 characters';
  if (!/^[a-z0-9._-]+$/.test(uname)) return 'Username may only use letters, numbers, and . _ -';
  if (!/^[a-z0-9]/.test(uname) || !/[a-z0-9]$/.test(uname)) return 'Username must start and end with a letter or number';
  if (/[._-]{2,}/.test(uname)) return 'Username cannot contain consecutive . _ - characters';
  if (RESERVED_USERNAMES.has(uname)) return 'That username is reserved';
  return null;
}

/**
 * Reduce a value to the characters a handle may contain, as the user types
 * (lowercase + [a-z0-9._-], capped at 30). Edge/length/reserved rules can't be
 * enforced mid-typing, so surface those via {@link validateUsername}.
 *
 * @param {string} raw
 * @returns {string}
 */
export function sanitizeUsernameInput(raw) {
  return String(raw || '').toLowerCase().replace(/[^a-z0-9._-]/g, '').slice(0, 30);
}
